import { IShapeImageView } from 'flux-diagram-composer';
import { Shape, Bitmap, DisplayObject } from '@creately/createjs-module';
import { AppConfig, Rectangle, Position, Logger } from 'flux-core';
import { IShapeImage, IRectangle, IShapeChild, ITransform } from 'flux-definition';

export class ShapeImageView implements IShapeImageView {
    private fileAtInit: string;
    private bitmap: Bitmap;
    private bitmapMask: Shape;
    private deferredUpdate: () => void;

    constructor( private imageModel: IShapeImage ) {
        this.fileAtInit = this.imageModel.file;
    }

    public get name(): string {
        return this.imageModel.id;
    }

    public get file(): string {
        return this.fileAtInit;
    }

    public get renderedImage() {
        return this.bitmap;
    }

    public get imageOutline() {
        return this.bitmapMask;
    }

    public initialize(): void {
        const imageElem = this.getImageElement();
        imageElem.crossOrigin = 'anonymous';
        imageElem.onload = () => this.onImageReady();
        imageElem.onerror = () => this.onImageError();
        imageElem.src = this.getImageUrl();
        this.bitmap = this.getBitmapShape( imageElem );
        this.bitmap.name = this.imageModel.id;
        this.setImageStoke();
    }

    public update( imageModel: IShapeImage, change?: any, shapeBounds?: Rectangle, transform?: ITransform ) {
        this.deferredUpdate = () => this.update( imageModel, change, shapeBounds, transform );
        this.imageModel = imageModel;
        const imageBounds = this.bitmap.getBounds();
        if ( !imageBounds ) {
            return;
        }
        this.resizeChild( shapeBounds, imageBounds, this.imageModel, this.bitmap, transform );
        this.positionChild( shapeBounds, imageBounds, this.imageModel, this.bitmap, transform );
        if ( this.bitmap.cacheID === null ) {
            this.bitmap.cache( 0, 0,
                imageBounds.width, imageBounds.height );
            this.bitmap.setBounds( imageBounds.x, imageBounds.y, imageBounds.width, imageBounds.height );
        }
        this.bitmap.updateCache();
        if ( this.imageModel.mask ) {
            if ( !this.bitmapMask ) {
                this.bitmapMask = new Shape();
                if ( this.imageModel.mask.type === 'square' ) {
                    this.bitmapMask.graphics.f( 'rgba(255, 0, 0, 0.5)' ).dr( 0, 0, 100, 100 );
                } else if ( this.imageModel.mask.type === 'roundedsquare' ) {
                    this.bitmapMask.graphics.f( 'rgba(255, 0, 0, 0.5)' ).drawRoundRect( 0, 0, 100, 100, 5 );
                } else {
                    this.bitmapMask.graphics.f( 'rgba(255, 0, 0, 1)' ).dc( 50, 50, 50 );
                }
                this.bitmapMask.setBounds( 0, 0, 100, 100 );
            }
            this.bitmap.mask = this.bitmapMask;
            const maskBounds = this.bitmapMask.getBounds();
            this.setImageStoke();
            this.resizeChild( shapeBounds, maskBounds, this.imageModel.mask, this.bitmapMask, transform );
            this.positionChild( shapeBounds, maskBounds, this.imageModel.mask, this.bitmapMask, transform );
            // NOTE: uncomment this to show masks on canvas
            // this.bitmap.parent.addChild( this.bitmapMask );
        }
    }

    public destroy(): void {
        // ...
    }

    private setImageStoke() {
        if ( this.imageModel.mask ) {
            if ( !this.bitmapMask ) {
                this.bitmapMask = new Shape();
            }
            this.bitmapMask.graphics.clear();
            if ( this.imageModel.mask?.stroke?.size > 0 ) {
                this.bitmapMask.graphics.
                setStrokeStyle( this.imageModel.mask.stroke.size ).
                s( this.imageModel.mask.stroke.color );
            }
            if ( this.imageModel.mask.fill ) {
                this.bitmapMask.graphics.f( this.imageModel.mask.fill );
            }
            if ( this.imageModel.mask.type === 'square' ) {
                this.bitmapMask.graphics
                .dr( 0, 0, 100, 100 );
            } else if ( this.imageModel.mask.type === 'roundedsquare' ) {
                this.bitmapMask.graphics
                .drawRoundRect( 0, 0, 100, 100, 5 );
            } else {
                this.bitmapMask.graphics
                .dc( 50, 50, 50 );
            }
            if ((this.imageModel.mask as any )?.objectFit === 'cover' && this.imageModel.mask?.w && this.imageModel.mask?.h ) {
                const canvasWidth = this.imageModel.mask.w.value;
                const canvasHeight = this.imageModel.mask.h.value;
                const imgRatio = this.bitmap.image.width / this.bitmap.image.height;
                const canvasRatio = canvasWidth / canvasHeight;

                let scale;
                if (imgRatio > canvasRatio) {
                    scale = canvasHeight / this.bitmap.image.height;
                } else {
                    scale = canvasWidth / this.bitmap.image.width;
                }
                this.bitmap.scaleX = this.bitmap.scaleY = scale;
            }
            this.bitmapMask.setBounds( 0, 0, 100, 100 );
        }
    }

    private getImageUrl(): string {
        if ( this.imageModel.fileType && this.imageModel.fileType === 'url' ) {
            return AppConfig.get( 'IMAGE_PROXY' ) + encodeURIComponent( this.fileAtInit );
        }
        if ( this.imageModel.fileType && this.imageModel.fileType === 'shapesassets' ) {
            return './assets/images/shapes-assets/' + this.fileAtInit;
        }
        return AppConfig.get( 'CUSTOM_IMAGE_BASE_URL' ) + this.fileAtInit;
    }

    /**
     * This method is called when the image load completes.
     */
    private onImageReady() {
        if ( this.deferredUpdate ) {
            this.deferredUpdate();
            this.deferredUpdate = null;
        }
        if ( this.bitmap.stage ) {
            this.bitmap.stage.update();
        }
    }

    /**
     * This method is called when the image load completes.
     */
    private onImageError() {
        // TODO: just log the load error?
    }

    /**
     * Retrieves a new Image instance
     */
    /* istanbul ignore next */
    private getImageElement(): HTMLImageElement {
        return new Image();
    }

    /**
     * Retrieves a new Bitmap instance
     */
    private getBitmapShape( image: HTMLImageElement ): Bitmap {
        return new Bitmap( image );
    }

    private resizeChild(
        shapeBounds: Rectangle,
        childBounds: IRectangle,
        childData: IShapeChild,
        childView: DisplayObject,
        transform: ITransform,
        ): void {

            if ( childData.w && childData.h ) {
                childView.scaleX = this.getExpectedScaleX( shapeBounds, childBounds, childData, transform );
                childView.scaleY = this.getExpectedScaleY( shapeBounds, childBounds, childData, transform );
            } else if ( childData.w ) {
                childView.scaleX = this.getExpectedScaleX( shapeBounds, childBounds, childData, transform );
                childView.scaleY = childView.scaleX;
            } else if ( childData.h ) {
                childView.scaleY = this.getExpectedScaleY( shapeBounds, childBounds, childData, transform );
                childView.scaleX = childView.scaleY;
            } else {
                childView.scaleX = 1;
                childView.scaleY = 1;
            }
    }

    private getExpectedScaleX(
        shapeBounds: Rectangle,
        childBounds: IRectangle,
        childData: IShapeChild,
        transform: ITransform,
    ): number {
        if ( childData.w.type === 'fixed' ) {
            const expectedWidth = childData.w.value;
            return expectedWidth / childBounds.width;
        } else if ( childData.w.type === 'relative' ) {
            const expectedWidth = shapeBounds.width * transform.scaleX * childData.w.value;
            return expectedWidth / childBounds.width;
        } else {
            Logger.error( 'ShapeImageView: invalid shape image (or mask) width type' );
            return 1;
        }
    }

    private getExpectedScaleY(
        shapeBounds: Rectangle,
        childBounds: IRectangle,
        childData: IShapeChild,
        transform: ITransform,
    ): number {
        if ( childData.h.type === 'fixed' ) {
            const expectedHeight = childData.h.value;
            return expectedHeight / childBounds.height;
        } else if ( childData.h.type === 'relative' ) {
            const expectedHeight = shapeBounds.height * transform.scaleY * childData.h.value;
            return expectedHeight / childBounds.height;
        } else {
            Logger.error( 'ShapeImageView: invalid shape image (or mask) height type' );
            return 1;
        }
    }

    private positionChild(
        shapeBounds: Rectangle,
        childBounds: IRectangle,
        childData: IShapeChild,
        childView: DisplayObject,
        transform: ITransform,
    ): void {
        const pointOnShape = Position.onRect({
            x: childData.x || { type: 'relative', value: 0 },
            y: childData.y || { type: 'relative', value: 0 },
        }, shapeBounds, transform );
        const pointOnImage = { x: 0, y: 0 };
        const offsets = { x: 0, y: 0 }
        if ( childData.alignX === 'l' ) {
            // no changes
        } else if ( childData.alignX === 'r' ) {
            pointOnImage.x = childBounds.width;
        } else {
            pointOnImage.x = childBounds.width / 2;
            /**
             * FIX ME: Come up with a formula that works not just for orgchart shapes
             */
            if ((this.imageModel.mask as any )?.objectFit === 'cover' && this.imageModel.mask.x.type === 'fixed-start' ) {
                offsets.x = this.imageModel.mask.x.value * 2.5;
            }
        }
        if ( childData.alignY === 't' ) {
            // no changes
        } else if ( childData.alignY === 'b' ) {
            pointOnImage.y = childBounds.height;
        } else {
            pointOnImage.y = childBounds.height / 2;
            // FIX ME: Come up with a formula that works not just for orgchart shapes
            if ((this.imageModel.mask as any )?.objectFit === 'cover' && this.imageModel.mask.y.type === 'fixed-start' && this.imageModel.mask.y.value === 15 ) {
                offsets.y = this.imageModel.mask.y.value * 2.5;
            }
        }
        childView.regX = pointOnImage.x;
        childView.regY = pointOnImage.y;
        childView.x = pointOnShape.x + offsets.x;
        childView.y = pointOnShape.y + offsets.y;
        childView.rotation = transform.angle;
    }
}
